import {
  BrowserCacheLocation,
  EventType,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
  type IPublicClientApplication
} from '@azure/msal-browser';
import type { Account } from '@/navigation/current-account';
import { DEMO_ACCOUNT } from '@/navigation/current-account';
import { readAuthConfig, type AuthConfig } from './auth-config';
import { AuthError } from './auth-error';

export interface AuthSnapshot {
  status:
    | 'loading'
    | 'signed-out'
    | 'authenticated'
    | 'redirecting'
    | 'error'
    | 'configuration-error'
    | 'interaction-required';
  mode: 'entra' | 'demo';
  account: Account | null;
  session: number;
  message?: string;
}

const accountKey = (account: AccountInfo) =>
  `${account.homeAccountId}:${account.localAccountId}:${account.tenantId}`;

const displayAccount = (account: AccountInfo): Account => ({
  name: account.name || account.username || 'Microsoft account',
  email: account.username,
  // Entra identity does not establish platform permissions or workspace membership.
  role: 'Microsoft account',
  workspace: ''
});

/** One SDK instance and one initialization promise per app, independent of React mounts. */
export const createAuthClient = (
  env: Record<string, unknown>,
  location: Pick<Location, 'origin' | 'href'>,
  makeMsal: (config: Configuration) => IPublicClientApplication = (config) =>
    new PublicClientApplication(config)
) => {
  let config: AuthConfig | undefined;
  let instance: IPublicClientApplication | undefined;
  let active: AccountInfo | null = null;
  let initialized = false;
  let initialization: Promise<void> | undefined;
  let interaction = false;
  let sessionAbort = new AbortController();
  const listeners = new Set<() => void>();
  let snapshot: AuthSnapshot = {
    status: 'loading',
    mode: 'entra',
    account: null,
    session: 0
  };

  const publish = (next: Omit<AuthSnapshot, 'session' | 'mode'>, invalidate = false) => {
    if (invalidate) {
      sessionAbort.abort();
      sessionAbort = new AbortController();
    }
    snapshot = {
      ...next,
      mode: config?.mode ?? 'entra',
      session: snapshot.session + Number(invalidate)
    };
    listeners.forEach((listener) => listener());
  };

  const syncAccount = (account: AccountInfo | null) => {
    const valid =
      account &&
      config?.mode === 'entra' &&
      account.tenantId.toLowerCase() === config.tenantId.toLowerCase()
        ? account
        : null;
    const changed = (active && accountKey(active)) !== (valid && accountKey(valid));
    active = valid;
    publish(
      {
        status: valid ? 'authenticated' : 'signed-out',
        account: valid ? displayAccount(valid) : null
      },
      changed
    );
  };

  try {
    config = readAuthConfig(env);
    if (config.mode === 'demo') {
      snapshot = {
        ...snapshot,
        mode: 'demo',
        status: 'authenticated',
        account: DEMO_ACCOUNT
      };
    } else {
      instance = makeMsal({
        auth: {
          clientId: config.clientId,
          authority: `https://login.microsoftonline.com/${config.tenantId}`,
          redirectUri: `${location.origin}/auth/callback`,
          postLogoutRedirectUri: location.origin
        },
        cache: { cacheLocation: BrowserCacheLocation.SessionStorage },
        system: { loggerOptions: { piiLoggingEnabled: false } }
      });
      instance.addEventCallback((event) => {
        if (
          initialized &&
          !interaction &&
          (event.eventType === EventType.ACTIVE_ACCOUNT_CHANGED ||
            event.eventType === EventType.RESTORE_FROM_BFCACHE)
        ) {
          syncAccount(instance!.getActiveAccount());
        }
      });
    }
  } catch {
    snapshot = {
      ...snapshot,
      status: 'configuration-error',
      message: 'Microsoft sign-in is not configured. Contact your administrator.'
    };
  }

  const initialize = (): Promise<void> => {
    if (!instance || !config || config.mode === 'demo') return Promise.resolve();
    const msal = instance;
    initialization ??= (async () => {
      try {
        await msal.initialize();
        const result = await msal.handleRedirectPromise();
        const accounts = msal
          .getAllAccounts()
          .filter(
            (account) =>
              config?.mode === 'entra' &&
              account.tenantId.toLowerCase() === config.tenantId.toLowerCase()
          );
        const account =
          result?.account ??
          msal.getActiveAccount() ??
          (accounts.length === 1 ? accounts[0] : null);
        msal.setActiveAccount(account);
        syncAccount(account);
      } catch {
        publish(
          {
            status: 'error',
            account: null,
            message: 'Sign-in could not be completed. Try again.'
          },
          true
        );
      } finally {
        initialized = true;
      }
    })();
    return initialization;
  };

  const requireSignIn = () => {
    if (snapshot.status !== 'authenticated' || config?.mode !== 'entra') return;
    publish(
      {
        status: 'interaction-required',
        account: null,
        message: 'Your session needs attention. Sign in again to continue.'
      },
      true
    );
  };

  const signIn = async () => {
    if (interaction || !instance || config?.mode !== 'entra') return;
    interaction = true;
    await initialize();
    publish({ status: 'redirecting', account: null }, true);
    try {
      await instance.loginRedirect({
        scopes: [config.apiScope],
        prompt: 'select_account',
        // MSAL owns state/nonce/PKCE and the return URL; no untrusted returnTo parameter.
        redirectStartPage:
          new URL(location.href).pathname === '/auth/callback'
            ? location.origin
            : location.href
      });
    } catch {
      publish({
        status: 'error',
        account: null,
        message: 'Microsoft sign-in could not start. Try again.'
      });
    } finally {
      interaction = false;
    }
  };

  const signOut = async () => {
    if (interaction || !instance || config?.mode !== 'entra') return;
    interaction = true;
    const account = active;
    active = null;
    // Unmount protected content and cancel requests before navigating to Microsoft.
    publish({ status: 'redirecting', account: null }, true);
    try {
      await instance.logoutRedirect({ account, postLogoutRedirectUri: location.origin });
    } catch {
      // A failed network logout must not restore the local app session.
      try {
        await instance.clearCache({ account: account ?? undefined });
        instance.setActiveAccount(null);
      } catch {
        // The app session remains invalid even if browser storage is unavailable.
      }
      publish({
        status: 'error',
        account: null,
        message: 'You are signed out of Ziggiz. Microsoft sign-out could not complete.'
      });
    } finally {
      interaction = false;
    }
  };

  const getCredentials = async () => {
    await initialize();
    if (
      !instance ||
      config?.mode !== 'entra' ||
      !active ||
      snapshot.status !== 'authenticated'
    ) {
      throw new AuthError(
        config?.mode === 'demo'
          ? 'API requests are unavailable in demo mode.'
          : 'Sign in to continue.'
      );
    }
    const account = active;
    const session = snapshot.session;
    const signal = sessionAbort.signal;
    const assertCurrent = () => {
      if (
        signal.aborted ||
        snapshot.session !== session ||
        snapshot.status !== 'authenticated'
      ) {
        throw new AuthError(
          'The account changed. Retry from the current session.',
          'session_changed'
        );
      }
    };
    try {
      const result = await instance.acquireTokenSilent({
        account,
        scopes: [config.apiScope],
        // Silent iframe responses use the isolated MSAL v5 bridge, never the router.
        redirectUri: `${location.origin}/auth/redirect.html`
      });
      assertCurrent();
      if (!result.account || accountKey(result.account) !== accountKey(account)) {
        requireSignIn();
        throw new AuthError(
          'The token belongs to a different account.',
          'session_changed'
        );
      }
      if (!result.accessToken) throw new AuthError('No API access token was issued.');
      return { token: result.accessToken, signal, assertCurrent };
    } catch (error) {
      assertCurrent();
      if (error instanceof InteractionRequiredAuthError) {
        requireSignIn();
        throw new AuthError('Sign in again to continue.');
      }
      throw error;
    }
  };

  return {
    initialize,
    signIn,
    signOut,
    getCredentials,
    requireSignIn,
    getSnapshot: () => snapshot,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    }
  };
};

export type AuthClient = ReturnType<typeof createAuthClient>;
